import { RegistryModel, Row } from 'shared/lib';
import { createRegistryBottomBar } from './registry-bottom-bar';
import { createRegistryEmpty } from './registry-empty';
import { createRegistryFilters } from './registry-filters';
import { createRegistryPagination } from './registry-pagination';
import { createRegistryPaginationControls } from './registry-pagination-controls';
import { createRegistryPaginationControlsEnd } from './registry-pagination-controls-end';
import { createRegistryPaginationControlsNext } from './registry-pagination-controls-next';
import { createRegistryPaginationControlsPrevious } from './registry-pagination-controls-previous';
import { createRegistryPaginationControlsStart } from './registry-pagination-controls-start';
import { createRegistryPaginationCounter } from './registry-pagination-counter';
import { createRegistryPaginationLimitSelect } from './registry-pagination-limit-select';
import { createRegistryRoot } from './registry-root';
import { createRegistrySelectionCounter } from './registry-selection-counter';
import { createRegistryTable } from './registry-table';
import { createRegistryTableCol } from './registry-table-col';
import { createRegistryTableColCheckbox } from './registry-table-col-checkbox';
import { createRegistryTableColgroup } from './registry-table-colgroup';
import { createRegistryTableEmpty } from './registry-table-empty';
import { createRegistryTableTbody } from './registry-table-tbody';
import { createRegistryTableTd } from './registry-table-td';
import { createRegistryTableTdCheckbox } from './registry-table-td-checkbox';
import { createRegistryTableTfoot } from './registry-table-tfoot';
import { createRegistryTableTh } from './registry-table-th';
import { createRegistryTableThCheckbox } from './registry-table-th-checkbox';
import { createRegistryTableThSorter } from './registry-table-th-sorter';
import { createRegistryTableThead } from './registry-table-thead';
import { createRegistryTableTr } from './registry-table-tr';
import { createRegistryTableTrList } from './registry-table-tr-list';
import { createRegistryTitle } from './registry-title';
import { createRegistryTopBar } from './registry-top-bar';

export const createRegistry = <
	TRow extends Row,
	TFilters extends Record<string, unknown>,
	TError,
	TAdditionalData = unknown,
>(
	model: RegistryModel<TRow, TFilters, TError, TAdditionalData>,
) => {
	const RegistryRoot = createRegistryRoot(model);
	const RegistryTitle = createRegistryTitle();
	const RegistryTopBar = createRegistryTopBar(model);
	const RegistryFilters = createRegistryFilters();
	const RegistrySelectionCounter = createRegistrySelectionCounter(model);
	const RegistryTable = createRegistryTable(model);
	const RegistryTableColgroup = createRegistryTableColgroup();
	const RegistryTableCol = createRegistryTableCol();
	const RegistryTableColCheckbox = createRegistryTableColCheckbox();
	const RegistryTableThead = createRegistryTableThead();
	const RegistryTableTbody = createRegistryTableTbody();
	const RegistryTableTfoot = createRegistryTableTfoot();
	const RegistryTableTr = createRegistryTableTr();
	const RegistryTableTrList = createRegistryTableTrList(model, RegistryTableTr);
	const RegistryTableTh = createRegistryTableTh();
	const RegistryTableThCheckbox = createRegistryTableThCheckbox(model);
	const RegistryTableThSorter = createRegistryTableThSorter(model);
	const RegistryTableTd = createRegistryTableTd();
	const RegistryTableTdCheckbox = createRegistryTableTdCheckbox(model);
	const RegistryTableEmpty = createRegistryTableEmpty(model);
	const RegistryBottomBar = createRegistryBottomBar(model);
	const RegistryPaginationLimitSelect = createRegistryPaginationLimitSelect(model);
	const RegistryPaginationCounter = createRegistryPaginationCounter(model);
	const RegistryPaginationControlsStart = createRegistryPaginationControlsStart(model);
	const RegistryPaginationControlsPrevious = createRegistryPaginationControlsPrevious(model);
	const RegistryPaginationControlsNext = createRegistryPaginationControlsNext(model);
	const RegistryPaginationControlsEnd = createRegistryPaginationControlsEnd(model);

	const RegistryPaginationControls = createRegistryPaginationControls(
		model,
		RegistryPaginationControlsStart,
		RegistryPaginationControlsPrevious,
		RegistryPaginationControlsNext,
		RegistryPaginationControlsEnd,
	);

	const RegistryPagination = createRegistryPagination(
		RegistryPaginationLimitSelect,
		RegistryPaginationCounter,
		RegistryPaginationControls,
	);

	const RegistryEmpty = createRegistryEmpty(model);

	return {
		Root: RegistryRoot,
		Title: RegistryTitle,
		TopBar: RegistryTopBar,
		Filters: RegistryFilters,
		Table: Object.assign(RegistryTable, {
			Colgroup: RegistryTableColgroup,
			Col: RegistryTableCol,
			ColCheckbox: RegistryTableColCheckbox,
			Thead: RegistryTableThead,
			Tbody: RegistryTableTbody,
			Tfoot: RegistryTableTfoot,
			Tr: RegistryTableTr,
			TrList: RegistryTableTrList,
			Td: RegistryTableTd,
			TdCheckbox: RegistryTableTdCheckbox,
			Th: RegistryTableTh,
			ThCheckbox: RegistryTableThCheckbox,
			ThSorter: RegistryTableThSorter,
			Empty: RegistryTableEmpty,
		}),
		BottomBar: RegistryBottomBar,
		SelectionCounter: RegistrySelectionCounter,
		Pagination: Object.assign(RegistryPagination, {
			LimitSelect: RegistryPaginationLimitSelect,
			Counter: RegistryPaginationCounter,
			Controls: Object.assign(RegistryPaginationControls, {
				Start: RegistryPaginationControlsStart,
				Previous: RegistryPaginationControlsPrevious,
				Next: RegistryPaginationControlsNext,
				End: RegistryPaginationControlsEnd,
			}),
		}),
		Empty: RegistryEmpty,
	};
};
